/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, computed, makeObservable, observable } from 'mobx';

import { DEFAULT_PAGINATION_LIMIT } from '../../../../constants';
import { PaginationMode } from '../../../enums';
import { PaginationState } from '../../../interfaces';
import { RequestSuccessResponse } from '../../../../../interfaces';

import type { GridProps, ExpandableItem } from '../../../interfaces';
import type { GridStore } from '../grid-store';

export class PaginationStore<RowDataT, TotalRowDataT, FilterT> {
    @observable.shallow data: RowDataT[] = [];
    @observable pagination: PaginationState = {
        activePage: 1,
        fullPages: 0,
        limit: undefined,
        start: 0,
    };
    @observable useCompactPagination: boolean;
    @observable useCompactPaginationWithCurrentPage: boolean;

    withPagination: boolean;

    constructor(
        private gridStore: GridStore<RowDataT, TotalRowDataT, FilterT>,
        props: GridProps<RowDataT, TotalRowDataT, FilterT>,
    ) {
        makeObservable(this);

        this.useCompactPagination = props.useCompactPagination ?? false;
        this.useCompactPaginationWithCurrentPage = props.useCompactPaginationWithCurrentPage ?? false;
        this.pagination.limit = props.paginationLimit ?? DEFAULT_PAGINATION_LIMIT;
        this.withPagination = !props.disablePagination;
    }

    @computed
    get pageData() {
        if (this.gridStore.expandedRowsStore.isExpandableGrid) {
            return this.gridStore.expandedRowsStore.transformExpandableDataToFlatList(this.data as ExpandableItem<RowDataT>[]);
        }

        return this.data;
    }

    @action.bound
    setPagination(pagination: Partial<PaginationState>): void {
        this.pagination = {
            ...this.pagination,
            ...pagination,
        };
    }

    @action.bound
    goToPage(page: number): void {
        const pageNumber = Math.max(1, page);

        this.pagination.activePage = Math.min(pageNumber, this.pagination.fullPages || 1);
        this.pagination.start = this.pagination.limit * (this.pagination.activePage - 1);
    }

    @action.bound
    clear(): void {
        this.data = [];
    }

    @action.bound
    updateData(response: RequestSuccessResponse<RowDataT[]>): void {
        this.data = response.data;
        this.pagination.fullPages = Math.ceil(response.meta.pagingInfo.total / this.pagination.limit);

        if (this.pagination.fullPages > 0 && this.pagination.activePage > this.pagination.fullPages) {
            const lastPage = this.pagination.fullPages;

            this.goToPage(lastPage);
        }

        if (this.gridStore.selectionStore.withSelection) {
            this.gridStore.selectionStore.selection.fullLength = response.meta.pagingInfo.total;
        }

        this.gridStore.selectionStore.checkIfAnyRowsNeedToBeSelected();
    }

    @action.bound
    changePaginationMode(paginationMode: PaginationMode): void {
        this.useCompactPagination = paginationMode === PaginationMode.Compact;
        this.useCompactPaginationWithCurrentPage = paginationMode === PaginationMode.CompactWithCurrentPage;
    }
}
